import { useCallback } from "react";

import { HubSupportTicketAttachmentList } from "@/components/HubSupportTicketAttachmentList";
import {
  fileToHubSupportLocalAttachment,
  HUB_SUPPORT_TICKET_FILE_ACCEPT,
  HUB_SUPPORT_TICKET_MAX_ATTACHMENTS,
  type HubSupportLocalAttachment,
} from "@/lib/hubSupportTicketAttachments";

export function HubSupportTicketAttachmentPicker({
  attachments,
  onChange,
  disabled = false,
}: {
  attachments: HubSupportLocalAttachment[];
  onChange: (next: HubSupportLocalAttachment[]) => void;
  disabled?: boolean;
}): JSX.Element {
  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = [...files];
      const next: HubSupportLocalAttachment[] = [];
      for (const file of list) {
        if (attachments.length + next.length >= HUB_SUPPORT_TICKET_MAX_ATTACHMENTS) break;
        const row = await fileToHubSupportLocalAttachment(file);
        if (row) next.push(row);
      }
      if (next.length > 0) {
        onChange([...attachments, ...next].slice(0, HUB_SUPPORT_TICKET_MAX_ATTACHMENTS));
      }
    },
    [attachments, onChange],
  );

  return (
    <div>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Files</span>
      <p className="mb-2 text-xs text-slate-500">
        Paste images here or choose files (images, PDF, text, CSV, Excel, Word, ZIP, or JSON, up to{" "}
        {HUB_SUPPORT_TICKET_MAX_ATTACHMENTS}).
      </p>
      <input
        type="file"
        accept={HUB_SUPPORT_TICKET_FILE_ACCEPT}
        multiple
        disabled={disabled}
        className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border file:border-slate-200 file:bg-slate-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-100"
        onChange={async (event) => {
          const list = event.target.files;
          if (list && list.length > 0) await addFiles(list);
          event.target.value = "";
        }}
      />
      {attachments.length > 0 ? (
        <div className="mt-3 space-y-2">
          <HubSupportTicketAttachmentList attachments={attachments} />
          <ul className="flex flex-wrap gap-3">
            {attachments.map((attachment) => (
              <li key={`remove-${attachment.id}`}>
                <button
                  type="button"
                  className="text-xs font-medium text-red-600 hover:text-red-700"
                  disabled={disabled}
                  onClick={() => onChange(attachments.filter((row) => row.id !== attachment.id))}
                >
                  Remove {attachment.fileName}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export async function addHubSupportTicketPastedFiles(
  items: DataTransferItemList | undefined,
  current: HubSupportLocalAttachment[],
  onChange: (next: HubSupportLocalAttachment[]) => void,
): Promise<boolean> {
  if (!items) return false;
  const files: File[] = [];
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (item?.kind === "file") {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  if (files.length === 0) return false;
  const next: HubSupportLocalAttachment[] = [];
  for (const file of files) {
    if (current.length + next.length >= HUB_SUPPORT_TICKET_MAX_ATTACHMENTS) break;
    const row = await fileToHubSupportLocalAttachment(file);
    if (row) next.push(row);
  }
  if (next.length === 0) return false;
  onChange([...current, ...next].slice(0, HUB_SUPPORT_TICKET_MAX_ATTACHMENTS));
  return true;
}
